/**
 * Proprietary road-segment layer.
 *
 * Point reports (community_tags) are user-attributed observations.
 * Road segments are *aggregated* signals across many users, snapped to
 * a quantized lat/lng grid (~50m at mid-latitudes). The aggregate row
 * exposes positive/negative scores separately so scoring can reward
 * segments people love AND penalize segments people consistently flag.
 */
import { supabase } from "@/integrations/supabase/client";
import { nearestPointOnPolyline, type LngLat } from "@/utils/polyline";

/** ~50m at mid-latitudes — small enough to be "this block", big enough
 *  that two reports a few storefronts apart still combine. */
const SEGMENT_PRECISION = 2000; // 1 / 0.0005 deg

export function segmentKey(lat: number, lng: number): string {
  const qLat = Math.round(lat * SEGMENT_PRECISION) / SEGMENT_PRECISION;
  const qLng = Math.round(lng * SEGMENT_PRECISION) / SEGMENT_PRECISION;
  return `${qLat.toFixed(4)},${qLng.toFixed(4)}`;
}

/** ~10m polyline-segment quantization. Tighter than the point grid so two
 *  adjacent route segments don't collapse onto the same key. */
const POLY_PRECISION = 10000; // 1 / 0.0001 deg

function qFix(v: number): string {
  return (Math.round(v * POLY_PRECISION) / POLY_PRECISION).toFixed(4);
}

/**
 * Build a deterministic key for an exact polyline edge between two
 * adjacent route coordinates. Endpoints are sorted so A→B and B→A collapse
 * onto the same key — a road feels equally pleasant in both directions.
 *
 * Format: `pl:{minLat},{minLng}|{maxLat},{maxLng}` (≤ 64 chars).
 */
export function polylineSegmentKey(a: LngLat, b: LngLat): string {
  const aKey = `${qFix(a[1])},${qFix(a[0])}`;
  const bKey = `${qFix(b[1])},${qFix(b[0])}`;
  const [first, second] = aKey <= bKey ? [aKey, bKey] : [bKey, aKey];
  return `pl:${first}|${second}`;
}

/**
 * Snap a point to the nearest edge of a route polyline and return the
 * segment's deterministic key + midpoint (used as the representative
 * lat/lng so the aggregated row sits ON the segment, where future routes
 * crossing the same stretch will pick it up via the bbox+nearestDistance
 * check inside communityScore).
 */
export function nearestPolylineSegment(
  point: LngLat,
  coords: LngLat[],
): { key: string; midLat: number; midLng: number; segmentIndex: number; distM: number } | null {
  if (coords.length < 2) return null;
  const snap = nearestPointOnPolyline(point, coords);
  const i = snap.segmentIndex;
  const a = coords[i];
  const b = coords[i + 1] ?? a;
  return {
    key: polylineSegmentKey(a, b),
    midLat: (a[1] + b[1]) / 2,
    midLng: (a[0] + b[0]) / 2,
    segmentIndex: i,
    // How far the raw point was from the route, metres. Callers use this to
    // decide whether a tag should snap to the road (GPS jitter beside it) or
    // stay raw (parked 500m away — snapping would invent a road they never
    // drove). Without this the caller can't tell those apart, which is how
    // tags ended up on houses and car parks.
    distM: snap.distance,
  };
}

export interface RoadSegment {
  segment_key: string;
  lat: number;
  lng: number;
  positive_count: number;
  negative_count: number;
  positive_score: number;
  negative_score: number;
  contributor_count: number;
  last_updated_at: string;
}

/**
 * Record a per-user contribution toward a road segment. The DB trigger
 * recomputes the aggregate row.
 *
 * Anon (logged-out) users cannot contribute to the proprietary layer —
 * we want accountability and de-duplication by user.
 *
 * `segmentKeyOverride` lets callers attach the rating to an exact polyline
 * edge of the route they're navigating instead of the coarse 50 m grid.
 * The aggregated row's lat/lng will converge on the average of the
 * contribution coords — passing the segment midpoint keeps it on the road.
 */
export async function contributeToSegment(args: {
  lat: number;
  lng: number;
  mode: "walking" | "biking" | "driving";
  sentiment: "positive" | "negative";
  tagLabel: string;
  segmentKeyOverride?: string;
}): Promise<void> {
  const { data: auth } = await supabase.auth.getUser();
  const userId = auth.user?.id;
  if (!userId) return; // silently skip — point report still landed

  const key = args.segmentKeyOverride ?? segmentKey(args.lat, args.lng);
  const { error } = await supabase.from("segment_contributions").insert({
    segment_key: key,
    user_id: userId,
    lat: args.lat,
    lng: args.lng,
    travel_mode: args.mode,
    sentiment: args.sentiment,
    tag_label: args.tagLabel.slice(0, 60),
  });
  if (error) console.warn("segment contribution failed:", error.message);
}

/** Fetch aggregated segments inside a bounding box. */
export async function fetchSegmentsInBbox(b: {
  minLat: number; maxLat: number; minLng: number; maxLng: number;
  minContributors?: number;
}): Promise<RoadSegment[]> {
  const { data, error } = await supabase.rpc("road_segments_in_bbox", {
    min_lat: b.minLat,
    max_lat: b.maxLat,
    min_lng: b.minLng,
    max_lng: b.maxLng,
    min_contributors: b.minContributors ?? 1,
  });
  if (error) {
    console.warn("road_segments_in_bbox failed:", error.message);
    return [];
  }
  return (data ?? []) as RoadSegment[];
}

// ---- in-memory cache so synchronous scoring can read these ----
const cache = new Map<string, RoadSegment>();

export function cachedSegment(lat: number, lng: number): RoadSegment | undefined {
  return cache.get(segmentKey(lat, lng));
}

export function cachedSegmentsInBbox(b: {
  minLat: number; maxLat: number; minLng: number; maxLng: number;
}): RoadSegment[] {
  const out: RoadSegment[] = [];
  for (const s of cache.values()) {
    if (s.lat >= b.minLat && s.lat <= b.maxLat && s.lng >= b.minLng && s.lng <= b.maxLng) {
      out.push(s);
    }
  }
  return out;
}

// Bbox-level dedupe: collapse repeated prefetches for the same (quantized)
// bbox within a short TTL, and share a single in-flight request across
// concurrent callers. Prevents the CommunityLayer rerender storm we saw
// firing dozens of identical road_segments_in_bbox RPCs per second.
const BBOX_TTL_MS = 30_000;
const bboxLastFetched = new Map<string, number>();
const bboxInFlight = new Map<string, Promise<void>>();

interface BboxMetric {
  fetches: number;     // actual RPC calls made
  ttlHits: number;     // skipped because within TTL
  inFlightHits: number; // joined an existing in-flight request
}
const bboxMetrics = new Map<string, BboxMetric>();
const totals: BboxMetric = { fetches: 0, ttlHits: 0, inFlightHits: 0 };

function bumpMetric(key: string, field: keyof BboxMetric) {
  const m = bboxMetrics.get(key) ?? { fetches: 0, ttlHits: 0, inFlightHits: 0 };
  m[field] += 1;
  bboxMetrics.set(key, m);
  totals[field] += 1;
}

/** Snapshot of dedupe/TTL stats — handy for ad-hoc inspection from devtools. */
export function getBboxFetchStats(): {
  totals: BboxMetric;
  perBbox: Record<string, BboxMetric>;
} {
  return { totals: { ...totals }, perBbox: Object.fromEntries(bboxMetrics) };
}

// Expose on window in dev so it's one keystroke away in the console.
if (typeof window !== "undefined" && import.meta.env.DEV) {
  (window as unknown as { __segmentBboxStats?: typeof getBboxFetchStats }).__segmentBboxStats =
    getBboxFetchStats;
}

// Throttle log lines so a noisy caller doesn't spam the console itself.
let lastLogAt = 0;
function maybeLog(reason: "fetch" | "ttl-hit" | "inflight-hit", key: string) {
  if (typeof console === "undefined") return;
  const now = Date.now();
  if (now - lastLogAt < 1000) return;
  lastLogAt = now;
   
  console.debug(
    `[segments] ${reason} bbox=${key} totals fetches=${totals.fetches} ttlHits=${totals.ttlHits} inFlightHits=${totals.inFlightHits}`,
  );
}

function bboxKey(b: { minLat: number; maxLat: number; minLng: number; maxLng: number }): string {
  // Quantize to ~10m so jittery viewport recenters collapse onto one key.
  const q = (n: number) => (Math.round(n * 10000) / 10000).toFixed(4);
  return `${q(b.minLat)},${q(b.minLng)}|${q(b.maxLat)},${q(b.maxLng)}`;
}

export async function prefetchSegmentsForBbox(b: {
  minLat: number; maxLat: number; minLng: number; maxLng: number;
}): Promise<void> {
  const key = bboxKey(b);
  const inFlight = bboxInFlight.get(key);
  if (inFlight) {
    bumpMetric(key, "inFlightHits");
    maybeLog("inflight-hit", key);
    return inFlight;
  }
  const last = bboxLastFetched.get(key) ?? 0;
  if (Date.now() - last < BBOX_TTL_MS) {
    bumpMetric(key, "ttlHits");
    maybeLog("ttl-hit", key);
    return;
  }

  bumpMetric(key, "fetches");
  maybeLog("fetch", key);
  const p = (async () => {
    try {
      const rows = await fetchSegmentsInBbox(b);
      for (const r of rows) cache.set(r.segment_key, r);
      bboxLastFetched.set(key, Date.now());
      notifySegmentsChanged();
    } finally {
      bboxInFlight.delete(key);
    }
  })();
  bboxInFlight.set(key, p);
  return p;
}

/**
 * Optimistically merge a fresh user rating into the in-memory road-segment
 * cache so route scores reflect it BEFORE the DB trigger + refetch lands.
 * Mirrors the server-side aggregation: weight 0.2, recency multiplier 1.0
 * (just-now), bumped into positive/negative counters by sentiment.
 */
export function applyOptimisticSegmentRating(args: {
  lat: number;
  lng: number;
  sentiment: "positive" | "negative";
  /** Per-contribution weight; matches the DB default. */
  weight?: number;
  /** Override the quantized point key with an exact polyline-segment key. */
  segmentKeyOverride?: string;
  /** Representative lat/lng for a brand-new aggregate row (e.g. segment
   *  midpoint). Falls back to the rating point. */
  representativeLat?: number;
  representativeLng?: number;
}): void {
  const w = args.weight ?? 0.2;
  const key = args.segmentKeyOverride ?? segmentKey(args.lat, args.lng);
  const prev = cache.get(key);
  const base: RoadSegment = prev ?? {
    segment_key: key,
    lat: args.representativeLat ?? args.lat,
    lng: args.representativeLng ?? args.lng,
    positive_count: 0,
    negative_count: 0,
    positive_score: 0,
    negative_score: 0,
    contributor_count: 0,
    last_updated_at: new Date().toISOString(),
  };
  const next: RoadSegment = {
    ...base,
    positive_count: base.positive_count + (args.sentiment === "positive" ? 1 : 0),
    negative_count: base.negative_count + (args.sentiment === "negative" ? 1 : 0),
    positive_score:  base.positive_score  + (args.sentiment === "positive" ? w : 0),
    negative_score:  base.negative_score  + (args.sentiment === "negative" ? w : 0),
    contributor_count: prev ? base.contributor_count : 1,
    last_updated_at: new Date().toISOString(),
  };
  cache.set(key, next);
  notifySegmentsChanged();
}

/** Lightweight pub/sub so UI can rescore in place when the cache changes. */
const ROAD_SEGMENTS_EVENT = "pleasant-routes:road-segments-updated";
function notifySegmentsChanged() {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new CustomEvent(ROAD_SEGMENTS_EVENT));
}
export function onRoadSegmentsChanged(cb: () => void): () => void {
  if (typeof window === "undefined") return () => {};
  const handler = () => cb();
  window.addEventListener(ROAD_SEGMENTS_EVENT, handler);
  return () => window.removeEventListener(ROAD_SEGMENTS_EVENT, handler);
}
